import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  { q: 'What is the Marketing Portal?', a: 'A multi-tenant email marketing platform built for travel agencies and agency networks. Each agency has its own brand, sender identity, contact list, and analytics — managed centrally if you operate several.' },
  { q: 'How do I get started?', a: 'Register, add your agency brand, point a sending domain at us for DKIM/SPF/DMARC, and import your contacts. Most agencies send their first campaign within the first week.' },
  { q: 'Can I try the platform before paying?', a: 'Yes. Use the View Demo link on the home page to walk through the live product with seeded data, no account required.' },
  { q: 'What email providers are supported?', a: 'We send via Amazon SES. You configure your own sending domain so every campaign goes out under your agency name and brand, not ours.' },
  { q: 'How are contacts managed?', a: 'Import via CSV or API. Tag and segment by trip history, engagement, custom fields, and tag combinations. Segments stay live as data flows in.' },
  { q: 'What analytics do I get?', a: 'Opens, clicks, bounces, complaints, unsubscribes — per campaign, per contact, and aggregated over time. Built for diagnosing deliverability and copy, not just vanity dashboards.' },
  { q: 'Is my data secure?', a: 'Yes. RS256-signed sessions, httpOnly cookies, CSRF protection, Argon2id password hashing, and per-tenant data isolation. AWS Secrets Manager holds signing keys; cookies never store credentials client-side.' },
  { q: 'Can I manage multiple agencies?', a: "Yes — that's the core use case. Switch between agencies in the top bar; permissions and assignments per agency are managed in admin settings." },
  { q: 'What support is available?', a: 'Email support on every plan; Professional and Enterprise plans include priority response and onboarding help with DNS, segmentation, and campaign templates.' },
  { q: 'How do I cancel?', a: 'Email us. Cancellations take effect at the end of the current billing period; we export your data on request.' },
];

const FAQ: React.FC = () => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <>
      <section className="text-center px-4 sm:px-6 py-16 sm:py-24 max-w-4xl mx-auto">
        <h1
          className="text-5xl sm:text-6xl md:text-7xl font-bold text-[var(--text)] leading-[1.05] tracking-tight mb-6"
          style={{ fontFamily: "'Quicksand', sans-serif" }}
        >
          Frequently asked<br />questions.
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[var(--text-muted)] max-w-2xl mx-auto">
          If your question isn't here, we'd rather hear from you than have you guess.
        </p>
      </section>

      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} className="bg-white border border-[var(--border)] rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[var(--bg)] transition-colors"
                >
                  <span className="font-medium text-[var(--text)]">{item.q}</span>
                  <span className={`text-[var(--text-muted)] transition-transform ${isOpen ? 'rotate-45' : ''}`}>+</span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 text-sm text-[var(--text-muted)] leading-relaxed border-t border-[var(--border)] pt-3">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="max-w-3xl mx-auto text-center mt-12">
          <p className="text-[var(--text-muted)] mb-4">Still curious?</p>
          <Link
            to="/help"
            className="bg-[var(--primary)] text-white px-7 py-3.5 rounded-lg font-semibold text-base hover:bg-[var(--primary-light)] transition-colors inline-block shadow-sm"
          >
            Contact us
          </Link>
        </div>
      </section>
    </>
  );
};

export default FAQ;
